import React, { useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosConfig";
import { TailSpin } from "react-loader-spinner";
import { API_URL } from "../../../constant";
import { formatAmount } from "../../../utils/FormatPerson";
import toast from "react-hot-toast";

interface CashbackDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: {
    duration: "weekly" | "monthly" | "yearly";
    targetAmount: number;
    cashbackAmount: number;
    status: "ongoing" | "inactive" | string;
    createdAt?: string | null;
  } | null;
  onSaved: (updated: {
    duration: "weekly" | "monthly" | "yearly";
    targetAmount: number;
    cashbackAmount: number;
    status: "ongoing" | "inactive" | string;
  }) => void;
  setRefetchCount: React.Dispatch<React.SetStateAction<number>>;
}

const CashbackDetailsModal: React.FC<CashbackDetailsModalProps> = ({
  open,
  onClose,
  data,
  onSaved,
  setRefetchCount,
}) => {
  const [saving, setSaving] = useState(false);

  const [rewardAmount, setRewardAmount] = useState<string>(
    data ? String(data.cashbackAmount) : ""
  );
  const [targetAmount, setTargetAmount] = useState<string>(
    data ? String(data.targetAmount) : ""
  );
  const [status, setStatus] = useState<string>(data?.status ?? "ongoing");

  React.useEffect(() => {
    if (data) {
      setRewardAmount(String(data.cashbackAmount));
      setTargetAmount(String(data.targetAmount));
      setStatus(data.status ?? "ongoing");
    }
  }, [data]);

  const formatWithCommas = (value: string) => {
    if (value === "") return "";
    const [intPart, decPart] = value.replace(/,/g, "").split(".");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
  };

  const createdText = useMemo(() => {
    if (!data?.createdAt) return "--";
    const date = new Date(data.createdAt);
    const day = date.getDate();
    const ordinalSuffix = (d: number) => {
      switch (d) {
        case 1:
        case 21:
        case 31:
          return "st";
        case 2:
        case 22:
          return "nd";
        case 3:
        case 23:
          return "rd";
        default:
          return "th";
      }
    };
    return `${date.toLocaleString("default", {
      month: "short",
    })} ${day}${ordinalSuffix(
      day
    )}, ${date.getFullYear()}, ${date.toLocaleString("default", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })}`;
  }, [data?.createdAt]);

  if (!open || !data) return null;

  const label = data.duration.charAt(0).toUpperCase() + data.duration.slice(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center font-manrope justify-center bg-black bg-opacity-80">
      <div className="bg-[#0D0D0D] rounded-2xl w-full max-w-lg p-0 shadow-lg relative">
        <div className="flex justify-between items-center px-8 pt-8 pb-4">
          <h2 className="text-white text-lg font-bold">Cashback Details</h2>
          <button
            onClick={onClose}
            className="text-[#60B7F6] text-base font-semibold"
          >
            Close
          </button>
        </div>

        <div className="px-6 pb-6 mt-2">
          <div className="rounded-xl grid grid-cols-2 gap-y-4 gap-x-6">
            <div className="text-[#AEBCC6] text-[15px]">Duration</div>
            <div className="text-white text-[15px] text-right font-semibold">
              {label}
            </div>

            <div className="text-[#AEBCC6] text-[15px]">Target Amount</div>
            <div className="text-white text-[15px] text-right font-semibold">
              ₦{" "}
              {formatAmount(
                Number(String(targetAmount).replace(/,/g, "")) || 0
              )}
            </div>

            <div className="text-[#AEBCC6] text-[15px]">Reward Amount</div>
            <div className="text-white text-[15px] text-right font-semibold">
              ₦{" "}
              {formatAmount(
                Number(String(rewardAmount).replace(/,/g, "")) || 0
              )}
            </div>

            <div className="text-[#AEBCC6] text-[15px]">Status</div>
            <div
              className={`text-right text-[13px] uppercase font-bold ${
                (status || "").toLowerCase() === "ongoing"
                  ? "text-[#22c55e]"
                  : (status || "").toLowerCase() === "inactive"
                  ? "text-[#ef4444]"
                  : "text-white"
              }`}
            >
              {status}
            </div>

            <div className="text-[#AEBCC6] text-[15px]">Created</div>
            <div className="text-white text-[15px] text-right font-semibold">
              {createdText}
            </div>
          </div>

          <div className="mt-6 border-t border-[#13181B] pt-6">
            <h3 className="text-white text-[15px] font-bold mb-3">Edit</h3>
            <div className="space-y-4">
              <div>
                <label className="text-white text-[14px] font-bold">
                  Target Amount
                </label>
                <div className="w-full flex justify-start mt-2 items-center gap-1 px-4 text-gray-200 bg-[#1C2227] border-[#141414] h-[46px] font-manrope border rounded-[8px]">
                  <h4>₦</h4>
                  <input
                    type="text"
                    value={formatWithCommas(targetAmount)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      if (/^\d*\.?\d*$/.test(raw)) setTargetAmount(raw);
                    }}
                    placeholder="0"
                    className="w-full outline-none py-3 text-[14px] text-gray-200 placeholder:text-gray-500 bg-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="text-white text-[14px] font-bold">
                  Reward Amount
                </label>
                <div className="w-full flex justify-start mt-2 items-center gap-1 px-4 text-gray-200 bg-[#1C2227] border-[#141414] h-[46px] font-manrope border rounded-[8px]">
                  <h4>₦</h4>
                  <input
                    type="text"
                    value={formatWithCommas(rewardAmount)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      if (/^\d*\.?\d*$/.test(raw)) setRewardAmount(raw);
                    }}
                    placeholder="0"
                    className="w-full outline-none py-3 text-[14px] text-gray-200 placeholder:text-gray-500 bg-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="text-white text-[14px] font-bold">
                  Status
                </label>
                <div className="mt-2 flex gap-3">
                  <button
                    onClick={() => setStatus("ongoing")}
                    className={`px-3 h-[36px] rounded-md text-xs font-semibold ${
                      (status || "").toLowerCase() === "ongoing"
                        ? "bg-[#0B3C1A] text-[#03C668]"
                        : "bg-[#1C2227] text-[#A0A9AD]"
                    }`}
                  >
                    Ongoing
                  </button>
                  <button
                    onClick={() => setStatus("inactive")}
                    className={`px-3 h-[36px] rounded-md text-xs font-semibold ${
                      (status || "").toLowerCase() === "inactive"
                        ? "bg-[#2A1313] text-[#C60303]"
                        : "bg-[#1C2227] text-[#A0A9AD]"
                    }`}
                  >
                    Inactive
                  </button>
                </div>
              </div>
              <button
                disabled={saving}
                onClick={async () => {
                  if (!data) return;
                  try {
                    setSaving(true);
                    const payload = {
                      cashbackAmount:
                        Number(String(rewardAmount).replace(/,/g, "")) || 0,
                      targetAmount:
                        Number(String(targetAmount).replace(/,/g, "")) || 0,
                      duration: data.duration,
                      status,
                    };
                    await axiosInstance.put(
                      `${API_URL}api/admin/cashback`,
                      payload
                    );
                    onSaved({
                      duration: data.duration,
                      cashbackAmount: payload.cashbackAmount,
                      targetAmount: payload.targetAmount,
                      status: payload.status,
                    });
                    toast.success("Cashback settings updated successfully");
                    setTimeout(() => {
                      setRefetchCount((prev: number) => prev + 1);
                      onClose();
                    }, 2000);
                  } catch (e: any) {
                    console.log(
                      e?.response?.data?.message ??
                        "Failed to update cashback settings"
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
                className="w-full h-[46px] flex justify-center items-center font-manrope font-medium rounded-[8px] text-white bg-secondary_button mt-2 disabled:opacity-60"
              >
                {saving ? (
                  <TailSpin color="#FFFFFF" height={30} width={30} />
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashbackDetailsModal;
